/**
 * Secure HTML Sanitizer
 * Uses DOMPurify to prevent XSS attacks in dangerouslySetInnerHTML
 */

import DOMPurify from 'dompurify';
import { BLOG_CONTENT_SANITIZE_CONFIG } from '../../shared/blog/htmlSanitizeConfig';
import { ensureBlogAnchorsHaveAccessibleText } from '../../shared/utils/ensureBlogAnchorsHaveAccessibleText';
import { demoteH1ToH2InUgcHtml } from '../../shared/utils/demoteH1InUgcHtml';

// Create a DOMPurify instance with strict configuration
const createPurify = () => {
  // For browser-side sanitization, use DOMPurify directly
  if (typeof window !== 'undefined') {
    return DOMPurify;
  }

  // For server-side, we'll handle this in the sanitizeHtml function
  return DOMPurify;
};

const purify = createPurify();

/**
 * Sanitize HTML content for safe rendering
 * @param html - The HTML content to sanitize
 * @param options - Additional sanitization options
 * @returns Sanitized HTML safe for dangerouslySetInnerHTML
 */
export async function sanitizeHtml(
  html: string,
  options?: {
    allowedTags?: string[];
    allowedAttributes?: string[];
    allowDataAttributes?: boolean;
  }
): Promise<string> {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const defaultOptions = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'b', 'i',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote',
      'a', 'img',
      'span', 'div',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      // Allow common formatting tags
      'mark', 'del', 'ins', 'sub', 'sup'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel',
      'src', 'alt', 'title', 'width', 'height',
      'class', 'id', 'style',
      'colspan', 'rowspan'
    ],
    ALLOW_DATA_ATTR: false,
    // Remove potentially dangerous attributes
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onmouseout', 'onmouseenter', 'onmouseleave'],
    // Remove script and style content
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button']
  };

  const sanitizeOptions = {
    ...defaultOptions,
    ...(options?.allowedTags && { ALLOWED_TAGS: options.allowedTags }),
    ...(options?.allowedAttributes && { ALLOWED_ATTR: options.allowedAttributes }),
    ...(options?.allowDataAttributes !== undefined && { ALLOW_DATA_ATTR: options.allowDataAttributes })
  };

  // Handle server-side sanitization
  if (typeof window === 'undefined') {
    try {
      // For server-side, we'll use a basic sanitization approach
      // since JSDOM might not be available in all environments
      return demoteH1ToH2InUgcHtml(purify.sanitize(html, sanitizeOptions));
    } catch (error) {
      // Fallback to basic string sanitization if DOMPurify fails
      const stripped = html.replace(/<script[^>]*>.*?<\/script>/gi, '')
                 .replace(/<style[^>]*>.*?<\/style>/gi, '')
                 .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
                 .replace(/javascript:/gi, '')
                 .replace(/on\w+="[^"]*"/gi, '');
      return demoteH1ToH2InUgcHtml(stripped);
    }
  }

  return demoteH1ToH2InUgcHtml(purify.sanitize(html, sanitizeOptions));
}

/**
 * Sanitize advertisement HTML content
 * @param html - The advertisement HTML content
 * @returns Sanitized HTML safe for ad rendering
 */
export async function sanitizeAdHtml(html: string): Promise<string> {
  return sanitizeHtml(html, {
    allowedTags: [
      'div', 'span', 'p', 'a', 'img', 'br',
      'strong', 'em', 'u', 'b', 'i',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'svg', 'path', 'circle', 'rect', 'polygon', 'polyline'
    ],
    allowedAttributes: [
      'href', 'target', 'rel',
      'src', 'alt', 'title',
      'class', 'id', 'style',
      'width', 'height', 'viewBox', 'fill', 'stroke', 'd', 'cx', 'cy', 'r', 'x', 'y', 'points'
    ]
  });
}

/**
 * Sanitize blog content HTML
 * @param html - The blog content HTML
 * @returns Sanitized HTML safe for blog rendering
 */
export async function sanitizeBlogHtml(html: string): Promise<string> {
  if (!html || typeof html !== 'string') {
    return '';
  }
  const cleaned = demoteH1ToH2InUgcHtml(purify.sanitize(html, BLOG_CONTENT_SANITIZE_CONFIG));
  return ensureBlogAnchorsHaveAccessibleText(cleaned);
}

/**
 * Sanitize email content (for display purposes)
 * @param html - The email HTML content
 * @returns Sanitized HTML safe for email display
 */
export async function sanitizeEmailHtml(html: string): Promise<string> {
  return sanitizeHtml(html, {
    allowedTags: [
      'p', 'br', 'strong', 'em', 'u', 'b', 'i',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'span', 'div',
      'table', 'thead', 'tbody', 'tr', 'th', 'td'
    ],
    allowedAttributes: [
      'href', 'target', 'rel',
      'class', 'id', 'style',
      'colspan', 'rowspan'
    ]
  });
}

/**
 * Create a React-safe sanitized HTML object for dangerouslySetInnerHTML
 * @param html - The HTML content to sanitize and wrap
 * @param sanitizer - The sanitization function to use (default: sanitizeHtml)
 * @returns Object safe for dangerouslySetInnerHTML
 */
export async function createSanitizedHtml(
  html: string,
  sanitizer?: (html: string) => Promise<string>
): Promise<{ __html: string }> {
  const sanitizeFn = sanitizer || sanitizeHtml;
  return {
    __html: await sanitizeFn(html)
  };
}

// Synchronous versions for backward compatibility (use browser DOMPurify)
export function sanitizeHtmlSync(
  html: string,
  options?: {
    allowedTags?: string[];
    allowedAttributes?: string[];
    allowDataAttributes?: boolean;
  }
): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const defaultOptions = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'b', 'i',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote',
      'a', 'img',
      'span', 'div',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'mark', 'del', 'ins', 'sub', 'sup'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel',
      'src', 'alt', 'title', 'width', 'height',
      'class', 'id', 'style',
      'colspan', 'rowspan'
    ],
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onmouseout', 'onmouseenter', 'onmouseleave'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button']
  };

  const sanitizeOptions = {
    ...defaultOptions,
    ...(options?.allowedTags && { ALLOWED_TAGS: options.allowedTags }),
    ...(options?.allowedAttributes && { ALLOWED_ATTR: options.allowedAttributes }),
    ...(options?.allowDataAttributes !== undefined && { ALLOW_DATA_ATTR: options.allowDataAttributes })
  };

  return demoteH1ToH2InUgcHtml(purify.sanitize(html, sanitizeOptions));
}

export function sanitizeAdHtmlSync(html: string): string {
  return sanitizeHtmlSync(html, {
    allowedTags: [
      'div', 'span', 'p', 'a', 'img', 'br',
      'strong', 'em', 'u', 'b', 'i',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'svg', 'path', 'circle', 'rect', 'polygon', 'polyline'
    ],
    allowedAttributes: [
      'href', 'target', 'rel',
      'src', 'alt', 'title',
      'class', 'id', 'style',
      'width', 'height', 'viewBox', 'fill', 'stroke', 'd', 'cx', 'cy', 'r', 'x', 'y', 'points'
    ]
  });
}

export function sanitizeBlogHtmlSync(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }
  const cleaned = demoteH1ToH2InUgcHtml(purify.sanitize(html, BLOG_CONTENT_SANITIZE_CONFIG));
  return ensureBlogAnchorsHaveAccessibleText(cleaned);
}

/**
 * Sanitize lesson plan body HTML (Comprehensive Body of Material).
 * Allows formatting, lists, images, links, and citation spans with data-citation-id.
 */
export function sanitizeLessonPlanBodyHtmlSync(html: string): string {
  return sanitizeHtmlSync(html, {
    allowedTags: [
      'p', 'br', 'strong', 'em', 'u', 'b', 'i', 's',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote',
      'a', 'img', 'span', 'div',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'mark', 'del', 'ins', 'sub', 'sup',
      'code', 'pre', 'hr'
    ],
    allowedAttributes: [
      'href', 'target', 'rel',
      'src', 'alt', 'title', 'width', 'height',
      'class', 'id', 'style',
      'colspan', 'rowspan',
      'data-citation-id'
    ],
    allowDataAttributes: true
  });
}

export function createSanitizedHtmlSync(
  html: string,
  sanitizer?: (html: string) => string
): { __html: string } {
  const sanitizeFn = sanitizer || sanitizeHtmlSync;
  return {
    __html: sanitizeFn(html)
  };
}

// Legacy function for backward compatibility
export function sanitizeAdHtmlSafe(html: string): string {
  return sanitizeAdHtmlSync(html);
}
